import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from './cn';

/**
 * The button. Authored the shadcn way — CVA variants over Tailwind utilities,
 * `asChild` for polymorphism — but the prop names are OURS and predate shadcn:
 * `primary | secondary | ghost | danger` is what 100+ call sites already pass.
 *
 * shadcn's own names (`default`, `destructive`, `outline`, `link`) are accepted
 * as well, so a component copied from the shadcn docs drops in unedited. They
 * are aliases, not a second design: `default` IS `primary`, `destructive` IS
 * `danger`.
 *
 * The one focus declaration here is not a second ring — it is the global one
 * restated, because this component's own `shadow-[…]` utility would otherwise
 * override it. Never add a `focus-visible:ring-*` on top: that WOULD be a
 * second ring.
 */
export const buttonVariants = cva(
  [
    'relative inline-flex items-center justify-center gap-[var(--space-2)]',
    'rounded-[var(--radius-md)] border border-transparent',
    'font-[var(--weight-semibold)] tracking-[var(--tracking-snug)] leading-[1.2]',
    'whitespace-nowrap select-none',
    'transition-[background-color,border-color,color,box-shadow,transform]',
    'duration-[var(--duration-fast)] ease-[var(--ease-out)]',
    'active:translate-y-[0.5px] active:scale-[0.985]',
    // Disabled is a SHAPE change, not a dimmer. Fading a teal button to 55%
    // produces a pale teal that reads as "working on it", and the two states
    // then look the same at a glance. Every variant instead flattens to the
    // muted surface with muted text, and the shadow goes — an unavailable
    // control should not look raised.
    'disabled:cursor-not-allowed disabled:shadow-none',
    'aria-busy:cursor-progress',
    // The focus ring, re-stated. tokens.css owns one global *:focus-visible
    // rule, but the variants below set `shadow-[…]` — a Tailwind utility, and
    // utilities are emitted last and unlayered, so at equal specificity that
    // resting shadow BEATS the global ring and the button focuses invisibly.
    // Same value, from the same token; see the long note in tokens.css.
    'focus-visible:shadow-[var(--ring-focus)]',
    // A finger is not a cursor: every size reaches the 44px touch minimum on
    // touch, except `sm`, which becomes merely compact rather than tiny.
    'coarse:min-h-[var(--tap-min)]',
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-[var(--brand-700)] text-[color:var(--text-on-brand)] shadow-[var(--shadow-xs)]',
          'hover:not-disabled:bg-[var(--brand-800)]',
          'disabled:bg-[var(--surface-muted)] disabled:text-[color:var(--text-muted)]',
        ].join(' '),
        secondary: [
          'border-[color:var(--input)] bg-[var(--surface-elevated)] text-[color:var(--text-primary)] shadow-[var(--shadow-xs)]',
          'hover:not-disabled:border-[color:var(--border-strong)] hover:not-disabled:bg-[var(--surface-subtle)]',
          'disabled:border-[color:var(--border-subtle)] disabled:bg-[var(--surface-subtle)] disabled:text-[color:var(--text-muted)]',
        ].join(' '),
        outline: [
          'border-[color:var(--border-strong)] bg-transparent text-[color:var(--text-primary)]',
          'hover:not-disabled:bg-[var(--surface-subtle)]',
          'disabled:border-[color:var(--border-default)] disabled:text-[color:var(--text-muted)]',
        ].join(' '),
        ghost: [
          'bg-transparent text-[color:var(--text-secondary)]',
          'hover:not-disabled:bg-[var(--surface-canvas-tint)] hover:not-disabled:text-[color:var(--text-primary)]',
          'disabled:text-[color:var(--text-muted)]',
        ].join(' '),
        danger: [
          'bg-[var(--danger-fg)] text-[color:var(--text-on-brand)] shadow-[var(--shadow-xs)]',
          'hover:not-disabled:bg-[var(--danger-fg-strong)]',
          'disabled:bg-[var(--surface-muted)] disabled:text-[color:var(--text-muted)]',
        ].join(' '),
        link: 'bg-transparent text-[color:var(--brand-700)] underline-offset-4 hover:underline disabled:text-[color:var(--text-muted)]',
      },
      // Height comes from --control-h*, shared with every form control, so a
      // button and the field beside it are the same height by construction
      // rather than by two padding stacks that happen to agree.
      size: {
        sm: 'min-h-[var(--control-h-sm)] px-[var(--space-2-5)] py-[var(--space-1)] text-[length:var(--text-xs)] coarse:min-h-9',
        md: 'min-h-[var(--control-h)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)]',
        lg: 'min-h-[var(--control-h-lg)] px-[var(--space-5)] py-[var(--space-2-5)] text-[length:var(--text-base)]',
        icon: 'size-[var(--control-h)] p-0 text-[length:var(--text-sm)] coarse:min-w-[var(--tap-min)]',
      },
      fullWidth: {
        true: 'flex w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  },
);

/** shadcn's vocabulary, mapped onto ours. */
const ALIAS = {
  default: 'primary',
  destructive: 'danger',
} as const;

type OwnVariant = NonNullable<VariantProps<typeof buttonVariants>['variant']>;
type OwnSize = NonNullable<VariantProps<typeof buttonVariants>['size']>;

export type ButtonVariant = OwnVariant | keyof typeof ALIAS;
export type ButtonSize = OwnSize | 'default';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  /** 'md' is the historical default; 'sm' for toolbars and toasts, 'lg' for
   *  the one primary action on a phone form. Every size grows to the 44px
   *  touch minimum on a coarse pointer. */
  size?: ButtonSize;
  /** Stretch to the container — a phone form's submit, a sheet's footer. */
  fullWidth?: boolean;
  loading?: boolean;
  /** What the button says while `loading` — "Reading the paper…" beats a
   *  generic "Loading…" when the wait has a name. */
  loadingLabel?: string | undefined;
  /** Render the child element instead of a <button> — for a link that should
   *  look like a button. Ignored while `loading`, which needs its own markup. */
  asChild?: boolean;
  children?: React.ReactNode;
}

function resolveVariant(v: ButtonVariant): OwnVariant {
  return v in ALIAS ? ALIAS[v as keyof typeof ALIAS] : (v as OwnVariant);
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    fullWidth = false,
    loading = false,
    loadingLabel,
    asChild = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = cn(
    buttonVariants({
      variant: resolveVariant(variant),
      size: size === 'default' ? 'md' : size,
      fullWidth,
    }),
    className,
  );

  if (asChild && !loading) {
    return (
      <Slot ref={ref} className={classes} {...rest}>
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      className={classes}
      {...rest}
    >
      {loading ? (
        <>
          <span
            aria-hidden="true"
            className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent opacity-70"
          />
          <span>{loadingLabel ?? 'Loading…'}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
});
